import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, CheckCircle, AlertTriangle, XCircle,
  ChevronLeft, ChevronRight, FileText, RefreshCw, Printer, Lock, Eye, X,
} from 'lucide-react';
import apiService from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { gerarHtmlContrato } from '../utils/gerarContratoCompraVenda';
import FotoMiniatura from '../components/FotoMiniatura';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function ResultBadge({ resultado }) {
  if (resultado === 'aprovado')
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium bg-green-100 text-green-800 px-2 py-0.5 rounded whitespace-nowrap">
        <CheckCircle size={11} />Aprovado
      </span>
    );
  if (resultado === 'aprovado_ressalvas')
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium bg-amber-100 text-amber-800 px-2 py-0.5 rounded whitespace-nowrap">
        <AlertTriangle size={11} />C/ ressalvas
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium bg-red-100 text-red-800 px-2 py-0.5 rounded whitespace-nowrap">
      <XCircle size={11} />Reprovado
    </span>
  );
}

function fmt(valor) {
  if (valor == null) return '—';
  return Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtData(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function fmtDataHora(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

const FAIXA_RENDA_LABEL = {
  ate_1sm:   'Até 1 salário mínimo',
  '1_a_3sm': '1 a 3 salários mínimos',
  '3_a_5sm': '3 a 5 salários mínimos',
  acima_5sm: 'Acima de 5 salários mínimos',
};

const TEMPO_CLIENTE_LABEL = {
  novo:        'Primeira compra',
  ate_1ano:    'Menos de 1 ano',
  '1_a_3anos': '1 a 3 anos',
  acima_3anos: 'Mais de 3 anos',
};

const SPC_SITUACAO_LABEL = {
  nao_consultado: 'Não consultado',
  positivo:       'Nada consta',
  negativado:     'Negativado',
};

// faixa_renda é gravada como valor numérico (atual) ou enum string (legado)
function rendaLabel(faixa) {
  if (faixa == null || faixa === '') return '—';
  const num = parseFloat(faixa);
  if (!isNaN(num)) return `R$ ${fmt(num)}`;
  return FAIXA_RENDA_LABEL[faixa] ?? faixa;
}

function formatarDocumento(digitos) {
  const d = String(digitos ?? '');
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return d;
}

// Monta o cliente no formato do ERP (o que gerarHtmlContrato espera) a partir
// dos dados gravados na análise
function clienteDaAnalise(a) {
  const doc = String(a.cpf_cliente ?? '');
  return {
    Nome:     a.nome_cliente,
    CPF:      doc.length === 11 ? formatarDocumento(doc) : undefined,
    CNPJ:     doc.length === 14 ? formatarDocumento(doc) : undefined,
    Fone_1:   a.cliente_fone ?? undefined,
    Endereco: a.cliente_endereco ?? undefined,
    Cidade:   a.cliente_cidade ?? undefined,
    Estado:   a.cliente_uf ?? undefined,
    Mae:      a.cliente_mae ?? undefined,
  };
}

function Campo({ label, children, full }) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm text-gray-900 break-words">{children ?? '—'}</div>
    </div>
  );
}

function Secao({ titulo, children }) {
  return (
    <div className="border border-gray-200 rounded-xl p-4">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">{titulo}</p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">{children}</div>
    </div>
  );
}

const SLOTS_FOTO = [
  { coluna: 'foto_rosto_path',      label: 'Rosto do cliente' },
  { coluna: 'foto_doc_frente_path', label: 'Identidade (frente)' },
  { coluna: 'foto_doc_verso_path',  label: 'Identidade (verso)' },
  { coluna: 'foto_outro_path',      label: 'Outro' },
];

// ─── Modal de detalhes da análise ─────────────────────────────────────────────
// Mostra os dados exatamente como foram gravados no momento da liberação.

// podeVerArquivos: só admin/gerente abrem fotos e documentos (LGPD — necessidade)
function DetalhesAnaliseModal({ analise, podeVerArquivos, onClose }) {
  const [abrindoDoc, setAbrindoDoc] = useState(null);

  async function abrirDoc(filename) {
    setAbrindoDoc(filename);
    try {
      const resp = await apiService.analiseCredito.getDoc(filename);
      const url = URL.createObjectURL(resp.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      alert('Não foi possível abrir o documento.');
    } finally {
      setAbrindoDoc(null);
    }
  }

  const a = analise;
  const clienteCadastrado = a.codigo_cliente && String(a.codigo_cliente) !== '0';
  const entrada       = Number(a.valor_entrada) || 0;
  const total         = Number(a.valor_solicitado) || 0;
  const parcelas      = Number(a.num_parcelas) || 1;
  const financiado    = total - entrada;
  const valorParcela  = financiado / parcelas;
  const pctEntrada    = total > 0 ? Math.round((entrada / total) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Cabeçalho */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-medium text-gray-900 leading-tight">{a.nome_cliente}</h2>
            <p className="text-xs text-gray-500">
              Análise #{a.id} · {fmtDataHora(a.created_at)} · {a.nome_operador || '—'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <ResultBadge resultado={a.resultado} />
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-4">

          <Secao titulo="Cliente">
            <Campo label="Situação">
              {clienteCadastrado ? 'Cliente cadastrado no ERP' : 'Cliente novo (sem cadastro no ERP)'}
            </Campo>
            <Campo label="Código">{clienteCadastrado ? a.codigo_cliente : '—'}</Campo>
            <Campo label="CPF/CNPJ">{a.cpf_cliente ? formatarDocumento(a.cpf_cliente) : '—'}</Campo>
            <Campo label="Telefone">{a.cliente_fone || '—'}</Campo>
            <Campo label="Endereço" full>
              {a.cliente_endereco
                ? [a.cliente_endereco, [a.cliente_cidade, a.cliente_uf].filter(Boolean).join(' – ')].filter(Boolean).join(' · ')
                : '—'}
            </Campo>
            <Campo label="Nome da mãe">{a.cliente_mae || '—'}</Campo>
            <Campo label="Tempo como cliente">
              {TEMPO_CLIENTE_LABEL[a.tempo_cliente] ?? a.tempo_cliente}
            </Campo>
            <Campo label="Cliente fiel">{a.cliente_fiel ? 'Sim' : 'Não'}</Campo>
          </Secao>

          <Secao titulo="Condição de venda">
            <Campo label="Valor da venda">R$ {fmt(a.valor_solicitado)}</Campo>
            <Campo label="Valor aprovado">R$ {fmt(a.valor_aprovado)}</Campo>
            <Campo label="Entrada">
              R$ {fmt(entrada)}{entrada > 0 && ` (${pctEntrada}%)`}
            </Campo>
            <Campo label="Valor financiado">R$ {fmt(financiado)}</Campo>
            <Campo label="Parcelamento" full>
              {parcelas}x de R$ {fmt(valorParcela)}
            </Campo>
          </Secao>

          <Secao titulo="Pontuação">
            <Campo label="Score total">{a.score_total} / 100</Campo>
            <Campo label="Resultado"><ResultBadge resultado={a.resultado} /></Campo>
            <Campo label="Histórico ERP">{a.hist_pontuacao} / 65</Campo>
            <Campo label="Formulário">{a.form_pontuacao} / 35</Campo>
          </Secao>

          <Secao titulo="Histórico no crediário (ERP)">
            {Number(a.hist_total_parcelas) === 0 ? (
              <Campo label="Histórico" full>Nenhuma compra a prazo anterior</Campo>
            ) : (
              <>
                <Campo label="Total de parcelas">{a.hist_total_parcelas}</Campo>
                <Campo label="Pagas em dia">{a.hist_pagas_em_dia}</Campo>
                <Campo label="Atraso leve (até 15 dias)">{a.hist_atraso_leve}</Campo>
                <Campo label="Atraso grave (+30 dias)">{a.hist_atraso_grave}</Campo>
                <Campo label="Parcelas em aberto">{a.hist_parcelas_abertas}</Campo>
                <Campo label="Maior atraso">{a.hist_maior_atraso} dia(s)</Campo>
                <Campo label="Média de atraso">{a.hist_media_atraso} dia(s)</Campo>
              </>
            )}
          </Secao>

          <Secao titulo="SPC e renda">
            <Campo label="Situação SPC">
              {SPC_SITUACAO_LABEL[a.spc_situacao] ?? a.spc_situacao}
            </Campo>
            <Campo label="Renda informada no formulário">{rendaLabel(a.faixa_renda)}</Campo>
            <Campo label="Renda (SPC)">
              {a.spc_renda_mensal != null ? `R$ ${fmt(a.spc_renda_mensal)}` : '—'}
            </Campo>
            <Campo label="Fonte da renda">
              {a.spc_fonte_renda === 'documentado' ? 'Comprovado por documento' : 'Declarado pelo cliente'}
            </Campo>
            {a.spc_observacoes && <Campo label="Observações da consulta" full>{a.spc_observacoes}</Campo>}
          </Secao>

          <Secao titulo="Documentos e confirmação">
            <Campo label={`Identidade${a.doc_identidade_tipo ? ` (${a.doc_identidade_tipo === 'cnh' ? 'CNH' : 'RG + CPF'})` : ''}`}>
              {a.doc_identidade_path && !podeVerArquivos ? 'Anexado (restrito)' : a.doc_identidade_path ? (
                <button
                  onClick={() => abrirDoc(a.doc_identidade_path)}
                  disabled={abrindoDoc === a.doc_identidade_path}
                  className="text-blue-600 hover:underline disabled:opacity-60"
                >
                  {a.doc_identidade_nome || 'Abrir'}
                </button>
              ) : 'Não anexado'}
            </Campo>
            <Campo label="Comprovante de renda">
              {a.doc_comprovante_renda_path && !podeVerArquivos ? 'Anexado (restrito)' : a.doc_comprovante_renda_path ? (
                <button
                  onClick={() => abrirDoc(a.doc_comprovante_renda_path)}
                  disabled={abrindoDoc === a.doc_comprovante_renda_path}
                  className="text-blue-600 hover:underline disabled:opacity-60"
                >
                  {a.doc_comprovante_renda_nome || 'Abrir'}
                </button>
              ) : 'Não anexado'}
            </Campo>
            <Campo label="Telefone de confirmação">{a.telefone_confirmacao || '—'}</Campo>
            <Campo label="WhatsApp enviado">{a.zap_confirmacao_enviado ? 'Sim' : 'Não'}</Campo>
            {a.zap_resposta && <Campo label="Resposta do cliente" full>{a.zap_resposta}</Campo>}
          </Secao>

          {(a.observacoes || a.liberacao_forcada) ? (
            <Secao titulo="Observações e liberação">
              {a.observacoes && <Campo label="Observações" full>{a.observacoes}</Campo>}
              {a.liberacao_forcada ? (
                <>
                  <Campo label="Liberação forçada">
                    <span className="text-red-700 font-medium">Sim</span>
                  </Campo>
                  <Campo label="Liberado por">
                    {a.liberacao_forcada_por || '—'} · {fmtDataHora(a.liberacao_forcada_em)}
                  </Campo>
                  <Campo label="Justificativa" full>{a.motivo_liberacao_forcada}</Campo>
                </>
              ) : null}
            </Secao>
          ) : null}

          <Secao titulo="Fotos">
            {podeVerArquivos ? SLOTS_FOTO.map(f => (
              <FotoMiniatura key={f.coluna} arquivo={a[f.coluna]} label={f.label}
                carregar={apiService.analiseCredito.getDoc} onAbrir={abrirDoc} />
            )) : (
              <Campo label="Fotos registradas" full>
                {SLOTS_FOTO.filter(f => a[f.coluna]).length} de 4 · visualização restrita a administradores e gerentes
              </Campo>
            )}
            {a.arquivos_eliminados_em && (
              <Campo label="Eliminação (LGPD)" full>
                Fotos e documentos eliminados em {fmtDataHora(a.arquivos_eliminados_em)} pelo prazo de retenção
              </Campo>
            )}
            <Campo label="Pasta" full>
              {a.cpf_cliente ? `CPF/CNPJ ${a.cpf_cliente}` : '—'}
              {a.fotos_em && ` · fotos de ${fmtDataHora(a.fotos_em)}`}
            </Campo>
          </Secao>

          <Secao titulo="Contrato">
            <Campo label="Impresso">{a.impresso ? 'Sim' : 'Não'}</Campo>
            <Campo label="Ciência LGPD do cliente">
              {a.ciencia_lgpd_em
                ? `${fmtDataHora(a.ciencia_lgpd_em)} · ${a.ciencia_lgpd_por || '—'}`
                : 'Não registrada'}
            </Campo>
          </Secao>

        </div>
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function HistoricoAnalises() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const podeImprimir = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';

  const [imprimindoId, setImprimindoId] = useState(null);
  const [detalhe,      setDetalhe]      = useState(null);
  const [carregandoId, setCarregandoId] = useState(null);

  async function handleDetalhes(resumo) {
    setCarregandoId(resumo.id);
    try {
      const resp = await apiService.analiseCredito.buscarPorId(resumo.id);
      if (!resp.data?.data) throw new Error('Análise não encontrada');
      setDetalhe(resp.data.data);
    } catch {
      alert('Erro ao carregar os detalhes da análise.');
    } finally {
      setCarregandoId(null);
    }
  }

  // Reimpressão do contrato original da análise: mesmo texto, mesma data da
  // consulta/emissão e mesmos valores gravados — nunca o resultado recalculado.
  async function handleImprimir(resumo) {
    setImprimindoId(resumo.id);
    try {
      const respAnalise = await apiService.analiseCredito.buscarPorId(resumo.id);
      const analise = respAnalise.data?.data;
      if (!analise) throw new Error('Análise não encontrada');

      // Análises novas trazem o cadastro do dia gravado (snapshot); as antigas
      // buscam no ERP como antes — cliente manual antigo sai só com o nome
      let cliente = clienteDaAnalise(analise);
      if (!analise.cliente_endereco && analise.ordem_cliente) {
        try {
          const respCliente = await apiService.clientes.buscarPorOrdem(analise.ordem_cliente);
          if (respCliente.data?.data) cliente = respCliente.data.data;
        } catch {
          // sem cadastro no ERP (cliente manual) — imprime só com o nome gravado
        }
      }

      const html = gerarHtmlContrato({
        cliente,
        dataContrato: new Date(analise.created_at),
        valorTotal: analise.valor_solicitado,
        numParcelas: analise.num_parcelas,
        valorEntrada: analise.valor_entrada,
        nomeOperador: analise.nome_operador,
      });

      const win = window.open('', '_blank');
      win.document.write(html);
      win.document.close();

      if (!analise.impresso) {
        await apiService.analiseCredito.marcarImpresso(analise.id);
        setAnalises(prev => prev.map(x => x.id === analise.id ? { ...x, impresso: 1 } : x));
      }
    } catch {
      alert('Erro ao gerar o contrato para reimpressão.');
    } finally {
      setImprimindoId(null);
    }
  }

  // Filtros
  const [filtroNome,      setFiltroNome]      = useState('');
  const [filtroResultado, setFiltroResultado] = useState('');
  const [filtroOperador,  setFiltroOperador]  = useState('');

  // Dados
  const [analises,   setAnalises]   = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, pages: 1 });
  const [loading,    setLoading]    = useState(false);
  const [erro,       setErro]       = useState('');

  // ── Buscar ─────────────────────────────────────────────────────────────────
  const buscar = useCallback(async (page = 1) => {
    setLoading(true);
    setErro('');
    try {
      const resp = await apiService.analiseCredito.listarTodas({
        page,
        limit: 20,
        nome:      filtroNome.trim()      || undefined,
        resultado: filtroResultado        || undefined,
        operador:  filtroOperador.trim()  || undefined,
      });
      const d = resp.data;
      setAnalises(d.data ?? []);
      setPagination(d.pagination ?? { total: 0, page: 1, limit: 20, pages: 1 });
    } catch {
      setErro('Erro ao carregar análises. Verifique a conexão com o servidor.');
    } finally {
      setLoading(false);
    }
  }, [filtroNome, filtroResultado, filtroOperador]);

  // Carrega ao montar
  useEffect(() => { buscar(1); }, []); // eslint-disable-line

  function handleBuscar(e) {
    e.preventDefault();
    buscar(1);
  }

  function irPagina(p) {
    if (p < 1 || p > pagination.pages) return;
    buscar(p);
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-5xl mx-auto">

      {/* Cabeçalho */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft size={15} />Voltar
        </button>
        <div>
          <h1 className="text-xl font-medium text-gray-900 leading-tight">Histórico de Análises</h1>
          <p className="text-sm text-gray-500">Consulta de todas as análises de crediário realizadas</p>
        </div>
      </div>

      {/* Filtros */}
      <form onSubmit={handleBuscar} className="bg-white border border-gray-200 rounded-xl p-4 mb-5">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[180px]">
            <label className="text-xs text-gray-500 block mb-1">Nome do cliente</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
              placeholder="Buscar por nome..."
              value={filtroNome}
              onChange={e => setFiltroNome(e.target.value)}
            />
          </div>

          <div className="min-w-[160px]">
            <label className="text-xs text-gray-500 block mb-1">Resultado</label>
            <select
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
              value={filtroResultado}
              onChange={e => setFiltroResultado(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="aprovado">Aprovado</option>
              <option value="aprovado_ressalvas">C/ ressalvas</option>
              <option value="reprovado">Reprovado</option>
            </select>
          </div>

          <div className="min-w-[160px]">
            <label className="text-xs text-gray-500 block mb-1">Responsável</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
              placeholder="Nome do operador..."
              value={filtroOperador}
              onChange={e => setFiltroOperador(e.target.value)}
            />
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setFiltroNome('');
                setFiltroResultado('');
                setFiltroOperador('');
                // busca com filtros limpos imediatamente
                setTimeout(() => buscar(1), 0);
              }}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-500 hover:bg-gray-50 flex items-center gap-1.5"
            >
              <RefreshCw size={13} />Limpar
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100"
            >
              <Search size={14} />Buscar
            </button>
          </div>
        </div>
      </form>

      {/* Tabela */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">

        {/* Totalizador */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50">
          <span className="text-xs text-gray-500">
            {loading ? 'Carregando...' : `${pagination.total} análise(s) encontrada(s)`}
          </span>
          {pagination.pages > 1 && (
            <span className="text-xs text-gray-400">
              Página {pagination.page} de {pagination.pages}
            </span>
          )}
        </div>

        {erro && (
          <div className="px-5 py-8 text-center text-sm text-red-600">{erro}</div>
        )}

        {!erro && !loading && analises.length === 0 && (
          <div className="px-5 py-12 text-center text-sm text-gray-400">
            Nenhuma análise encontrada com os filtros informados.
          </div>
        )}

        {!erro && analises.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-gray-100">
                  <th className="text-left px-5 py-3 font-medium">Data</th>
                  <th className="text-left px-4 py-3 font-medium">Cliente</th>
                  <th className="text-right px-4 py-3 font-medium">Valor</th>
                  <th className="text-center px-4 py-3 font-medium">Resultado</th>
                  <th className="text-center px-4 py-3 font-medium">Impresso</th>
                  <th className="text-left px-4 py-3 font-medium">Responsável</th>
                  <th className="text-center px-4 py-3 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {analises.map(a => (
                  <tr key={a.id} className="hover:bg-gray-50 transition-colors">

                    {/* Data */}
                    <td className="px-5 py-3 whitespace-nowrap">
                      <div className="text-gray-900 font-medium">{fmtData(a.created_at)}</div>
                      <div className="text-xs text-gray-400">
                        {new Date(a.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </td>

                    {/* Cliente */}
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 truncate max-w-[220px]">{a.nome_cliente}</div>
                      <div className="text-xs text-gray-400">
                           {a.codigo_cliente && String(a.codigo_cliente) !== '0'
                             ? `Cód. ${a.codigo_cliente}`
                             : 'Cliente Novo'}
                      </div>
                    </td>

                    {/* Valor */}
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <span className="font-medium text-gray-900">
                        R$ {fmt(a.valor_aprovado)}
                      </span>
                    </td>

                    {/* Resultado */}
                    <td className="px-4 py-3 text-center">
                      <ResultBadge resultado={a.resultado} />
                    </td>

                    {/* Impresso */}
                    <td className="px-4 py-3 text-center">
                      {a.impresso ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-0.5">
                          <FileText size={11} />
                          Sim
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Não</span>
                      )}
                    </td>

                    {/* Responsável */}
                    <td className="px-4 py-3">
                      <span className="text-gray-700 text-sm">{a.nome_operador || '—'}</span>
                    </td>

                    {/* Ações */}
                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      <button
                        onClick={() => handleDetalhes(a)}
                        disabled={carregandoId === a.id}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 mr-1.5 border border-blue-300 bg-blue-50 rounded-lg text-xs text-blue-700 hover:bg-blue-100 transition-colors disabled:opacity-60 disabled:cursor-wait"
                      >
                        <Eye size={13} />{carregandoId === a.id ? 'Abrindo...' : 'Detalhes'}
                      </button>
                      {podeImprimir ? (
                        <button
                          onClick={() => handleImprimir(a)}
                          disabled={imprimindoId === a.id}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-green-300 bg-green-50 rounded-lg text-xs text-green-700 hover:bg-green-100 transition-colors disabled:opacity-60 disabled:cursor-wait"
                        >
                          <Printer size={13} />{imprimindoId === a.id ? 'Gerando...' : 'Imprimir'}
                        </button>
                      ) : (
                        <button
                          disabled
                          title="Apenas administradores ou gerentes podem imprimir análises."
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-100 rounded-lg text-xs text-gray-300 cursor-not-allowed"
                        >
                          <Lock size={13} />Imprimir
                        </button>
                      )}
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginação */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <button
              onClick={() => irPagina(pagination.page - 1)}
              disabled={pagination.page <= 1 || loading}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />Anterior
            </button>

            {/* Páginas */}
            <div className="flex gap-1">
              {Array.from({ length: Math.min(pagination.pages, 7) }, (_, i) => {
                // Janela de páginas ao redor da atual
                let p;
                if (pagination.pages <= 7) {
                  p = i + 1;
                } else if (pagination.page <= 4) {
                  p = i + 1;
                } else if (pagination.page >= pagination.pages - 3) {
                  p = pagination.pages - 6 + i;
                } else {
                  p = pagination.page - 3 + i;
                }
                return (
                  <button
                    key={p}
                    onClick={() => irPagina(p)}
                    disabled={loading}
                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors
                      ${p === pagination.page
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => irPagina(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages || loading}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Próxima<ChevronRight size={14} />
            </button>
          </div>
        )}

      </div>

      {detalhe && <DetalhesAnaliseModal analise={detalhe} podeVerArquivos={podeImprimir} onClose={() => setDetalhe(null)} />}
    </div>
  );
}